import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listEventSubmissions, fetchSubmissionThumbnailDataUrl, reviewSubmission, softDeleteSubmission, restoreSubmission, listSimilarSubmissions } from '@/lib/submissions-api';
import { ArrowLeft, Heart, BookOpen, Tv, Archive, RotateCcw, Eye, Trash2, ChevronLeft, ChevronRight, Copy, X, Video, Image } from 'lucide-react';

function ActionTile({ icon: Icon, label, active, accentColor, onClick, testId, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex flex-col items-start gap-2 p-3 rounded-xl border min-h-[96px] text-left transition-all hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] ${active ? 'border-current' : 'border-[#E3E8EF] bg-white'}`}
      style={active ? { borderColor: accentColor, backgroundColor: `${accentColor}18` } : {}}
      data-testid={testId}>
      <div className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ backgroundColor: active ? accentColor : '#EFF5FF' }}>
        <Icon className="w-5 h-5" style={{ color: active ? '#fff' : accentColor }} />
      </div>
      <span className="text-[13px] font-extrabold" style={{ color: active ? accentColor : '#0B1B46' }}>{label}</span>
    </button>
  );
}

export default function HostReviewPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';
  const focusId = sp.get('sub_id') || sp.get('focus_id') || '';
  const initialStatus = sp.get('status') || 'new';

  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [heroThumb, setHeroThumb] = useState(null);
  const [filmThumbs, setFilmThumbs] = useState({});
  const [similar, setSimilar] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarOpen, setSimilarOpen] = useState(false);
  const [similarThumbs, setSimilarThumbs] = useState({});

  const current = queue[idx] || null;

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true); setError(null);
    try {
      const page = await listEventSubmissions(eventId, { limit: 60, status: initialStatus, sort: 'uploaded_recent' });
      let subs = page.submissions || [];
      if (focusId) {
        let fi = subs.findIndex(s => s.id === focusId);
        if (fi === -1) {
          const all = await listEventSubmissions(eventId, { limit: 60, status: 'all', sort: 'uploaded_recent' });
          subs = all.submissions || [];
          fi = subs.findIndex(s => s.id === focusId);
        }
        if (fi >= 0) setIdx(fi); else setIdx(0);
      } else { setIdx(0); }
      setQueue(subs);
    } catch (e) { setError(e.message || "Couldn't load queue."); }
    finally { setLoading(false); }
  }, [eventId, initialStatus, focusId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  // Hero thumb
  useEffect(() => {
    if (!current) { setHeroThumb(null); return; }
    let cancelled = false;
    setHeroThumb(null);
    if (current.media_kind !== 'photo') return;
    (async () => { const u = await fetchSubmissionThumbnailDataUrl(current.id); if (!cancelled) setHeroThumb(u); })();
    return () => { cancelled = true; };
  }, [current?.id, current?.media_kind]);

  // Filmstrip thumbs
  useEffect(() => {
    let cancelled = false;
    const w = queue.slice(Math.max(0, idx - 3), Math.min(queue.length, idx + 4));
    (async () => {
      for (const s of w) {
        if (cancelled) return;
        if (s.media_kind !== 'photo' || filmThumbs[s.id] !== undefined) continue;
        const u = await fetchSubmissionThumbnailDataUrl(s.id);
        if (cancelled) return;
        setFilmThumbs(p => ({ ...p, [s.id]: u }));
      }
    })();
    return () => { cancelled = true; };
  }, [idx, queue.length]); // eslint-disable-line

  // Similar
  useEffect(() => {
    if (!current || current.media_kind !== 'photo') { setSimilar([]); return; }
    let cancelled = false;
    setSimilarLoading(true);
    (async () => {
      try { const r = await listSimilarSubmissions(current.id); if (!cancelled) setSimilar(r.similar || []); }
      catch { if (!cancelled) setSimilar([]); }
      finally { if (!cancelled) setSimilarLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [current?.id]);

  // Keyboard nav
  useEffect(() => {
    const h = (e) => {
      if (similarOpen) return;
      if (e.key === 'ArrowRight') setIdx(i => Math.min(queue.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [queue.length, similarOpen]);

  function updateCurrent(patch) { setQueue(p => p.map((s, i) => i === idx ? { ...s, ...patch } : s)); }

  async function applyAction(a) {
    if (!current) return;
    setBusy(true);
    try { const { submission } = await reviewSubmission(current.id, a); updateCurrent(submission); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function onDelete() {
    if (!current || !window.confirm('Delete this view? You can restore it for 30 days.')) return;
    setBusy(true);
    try { const { submission } = await softDeleteSubmission(current.id); updateCurrent(submission); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function onRestore() {
    if (!current) return;
    setBusy(true);
    try { const { submission } = await restoreSubmission(current.id); updateCurrent(submission); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function onCompareKeep() {
    if (!current || similar.length === 0) return;
    setBusy(true);
    try {
      await reviewSubmission(current.id, { favorite: true, mark_reviewed: true });
      for (const m of similar) await reviewSubmission(m.submission.id, { archive: true, mark_reviewed: true });
      updateCurrent({ favorited: true, reviewed: true });
      setSimilarOpen(false); setSimilar([]);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const canBack = idx > 0, canFwd = idx < queue.length - 1;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="host-review-screen">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="host-review-back"><ArrowLeft className="w-5 h-5 text-[#0B1B46]" /></button>
        <h1 className="text-xl font-extrabold text-[#0B1B46]">Review & Curate</h1>
        <div className="w-5" />
      </div>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="host-review-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : !current ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="host-review-empty">
          <p className="font-bold text-[#0B1B46]">Nothing to review</p>
          <p className="text-sm text-[#5B6472] mt-1">You&apos;re all caught up on this filter.</p>
          <button onClick={() => navigate(`/host-vault?event_id=${eventId}`)} className="h-10 px-6 mt-3 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8]" data-testid="host-review-back-to-vault">Back to Vault</button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-extrabold text-[#0B1B46]" data-testid="host-review-counter">{idx + 1} / {queue.length}</span>
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">High Resolution</span>
            {current.reviewed && <span className="px-2 py-0.5 text-xs font-bold bg-[#E4F4EC] text-[#1F8A55] rounded-full">✓ Reviewed</span>}
            {current.archived && <span className="px-2 py-0.5 text-xs font-bold bg-[#FBEEDA] text-[#B86500] rounded-full">Archived</span>}
            {current.deletion_pending && <span className="px-2 py-0.5 text-xs font-bold bg-[#FBE5E5] text-[#C93434] rounded-full">Deleted</span>}
          </div>

          {/* Hero */}
          <div className="relative w-full aspect-[3/2] rounded-xl overflow-hidden bg-[#0B1B46] flex items-center justify-center" data-testid="host-review-hero">
            {current.media_kind === 'video' ? (
              <div className="flex flex-col items-center gap-2"><Video className="w-12 h-12 text-white" /><p className="text-xs text-white">Video preview not yet supported</p></div>
            ) : heroThumb ? (
              <img src={heroThumb} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {current.deletion_pending && (
              <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-2 p-4">
                <p className="text-white font-extrabold text-base">Marked for deletion</p>
                <button onClick={onRestore} disabled={busy} className="h-10 px-6 bg-[#146BFF] text-white font-semibold rounded-lg" data-testid="host-review-restore-btn">Restore</button>
              </div>
            )}
            <button onClick={() => canBack && setIdx(idx - 1)} disabled={!canBack}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/35 flex items-center justify-center" data-testid="host-review-prev">
              <ChevronLeft className={`w-5 h-5 ${canBack ? 'text-white' : 'text-white/30'}`} />
            </button>
            <button onClick={() => canFwd && setIdx(idx + 1)} disabled={!canFwd}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/35 flex items-center justify-center" data-testid="host-review-next">
              <ChevronRight className={`w-5 h-5 ${canFwd ? 'text-white' : 'text-white/30'}`} />
            </button>
          </div>

          {/* Submitter */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-review-submitter">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#146BFF] flex items-center justify-center text-white font-extrabold text-base flex-shrink-0">
                {(current.contributor_first_name?.[0] || '?').toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-base font-extrabold text-[#0B1B46]" data-testid="host-review-contrib-name">{current.contributor_first_name || 'Attendee'}{current.contributor_last_name ? ` ${current.contributor_last_name}` : ''}</p>
                <p className="text-xs text-[#5B6472]">Submitted {current.delivered_at ? new Date(current.delivered_at).toLocaleString() : '—'}</p>
                {current.caption && <p className="text-sm text-[#0B1B46] italic mt-1" data-testid="host-review-caption">&ldquo;{current.caption}&rdquo;</p>}
              </div>
            </div>
          </div>

          {/* 2x2 actions */}
          <div className="grid grid-cols-2 gap-3" data-testid="host-review-actions">
            <ActionTile icon={Heart} label="Favorite" active={current.favorited} accentColor="#C93434" onClick={() => applyAction({ favorite: !current.favorited })} testId="host-review-favorite" disabled={busy} />
            <ActionTile icon={BookOpen} label="Add to Lookbook" active={current.lookbook_selected} accentColor="#146BFF" onClick={() => applyAction({ lookbook: !current.lookbook_selected })} testId="host-review-lookbook" disabled={busy} />
            <ActionTile icon={Tv} label="Add to Frame" active={current.frame_selected} accentColor="#0B1B46" onClick={() => applyAction({ frame: !current.frame_selected })} testId="host-review-frame" disabled={busy} />
            <ActionTile icon={current.archived ? RotateCcw : Archive} label={current.archived ? 'Restore from Archive' : 'Archive Photo'} active={current.archived} accentColor="#B86500" onClick={() => applyAction({ archive: !current.archived })} testId="host-review-archive" disabled={busy} />
          </div>

          {/* Compare similar */}
          {similar.length > 0 ? (
            <button onClick={() => setSimilarOpen(true)} className="flex items-center gap-3 p-4 rounded-xl bg-[#EFF5FF] border border-[#146BFF] text-left hover:bg-[#D6E4FF] transition-colors" data-testid="host-review-compare-row">
              <Copy className="w-5 h-5 text-[#146BFF]" />
              <div className="flex-1">
                <p className="text-sm font-extrabold text-[#0B1B46]">Compare Similar</p>
                <p className="text-xs text-[#5B6472]" data-testid="host-review-compare-count">{similar.length} near-duplicate{similar.length === 1 ? '' : 's'} found</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#5B6472]" />
            </button>
          ) : similarLoading ? (
            <div className="flex items-center gap-2 text-xs text-[#5B6472]"><div className="w-4 h-4 border-2 border-[#5B6472] border-t-transparent rounded-full animate-spin" /> Scanning for similar photos…</div>
          ) : null}

          {/* Secondary actions */}
          <div className="flex gap-3">
            <button onClick={() => applyAction({ mark_reviewed: true })} disabled={busy || current.reviewed}
              className="flex-1 h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] disabled:opacity-50 text-sm"
              data-testid="host-review-mark-reviewed">{current.reviewed ? 'Marked reviewed' : 'Mark Reviewed'}</button>
            <button onClick={current.deletion_pending ? onRestore : onDelete} disabled={busy}
              className="flex-1 h-10 bg-[#C93434] hover:bg-[#a02a2a] text-white font-semibold rounded-lg disabled:opacity-50 text-sm"
              data-testid="host-review-delete">{current.deletion_pending ? 'Deleted' : 'Delete'}</button>
          </div>

          {/* Filmstrip */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-review-filmstrip">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Quick review</p>
              <button onClick={() => navigate(`/host-vault?event_id=${eventId}`)} className="text-[#146BFF] text-xs font-bold hover:underline" data-testid="host-review-view-all">View All</button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {queue.slice(Math.max(0, idx - 3), Math.min(queue.length, idx + 6)).map(s => {
                const i = queue.indexOf(s);
                return (
                  <button key={s.id} onClick={() => setIdx(i)}
                    className={`w-[68px] h-[68px] rounded-md overflow-hidden flex-shrink-0 border-2 relative ${i === idx ? 'border-[#146BFF]' : 'border-transparent'}`}
                    data-testid={`host-review-film-${s.id}`}>
                    {s.media_kind === 'video' ? (
                      <div className="w-full h-full bg-[#0B1B46] flex items-center justify-center"><Video className="w-4 h-4 text-white" /></div>
                    ) : filmThumbs[s.id] ? (
                      <img src={filmThumbs[s.id]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#F3F5F8] flex items-center justify-center"><div className="w-4 h-4 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
                    )}
                    {s.favorited && <Heart className="absolute bottom-1 right-1 w-2.5 h-2.5 fill-red-500 text-red-500" />}
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="host-review-error">{error}</div>}
        </div>
      )}

      {/* Compare modal */}
      {similarOpen && (
        <div className="fixed inset-0 z-50 bg-[#0B1B46]/55 flex items-end justify-center">
          <div className="bg-white rounded-t-2xl p-6 w-full max-w-2xl max-h-[82vh] overflow-y-auto" data-testid="host-review-compare-modal">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-extrabold text-[#0B1B46]">Compare Similar</h2>
              <button onClick={() => setSimilarOpen(false)} data-testid="host-review-compare-close"><X className="w-5 h-5 text-[#0B1B46]" /></button>
            </div>
            <p className="text-sm text-[#5B6472] mb-4">We&apos;ll never delete automatically. Pick the keeper — the others get archived.</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              <div className="w-[180px] flex-shrink-0">
                <p className="text-[11px] font-extrabold text-[#5B6472] tracking-wider mb-1">Current</p>
                {heroThumb ? <img src={heroThumb} alt="" className="w-[180px] h-[120px] rounded-lg object-cover" /> : <div className="w-[180px] h-[120px] rounded-lg bg-[#F3F5F8]" />}
                <p className="text-xs font-bold text-[#0B1B46] mt-1">{current?.contributor_first_name}</p>
              </div>
              {similar.map(m => (
                <div key={m.submission.id} className="w-[180px] flex-shrink-0" data-testid={`host-review-compare-item-${m.submission.id}`}>
                  <p className="text-[11px] font-extrabold text-[#5B6472] tracking-wider mb-1">Similar · Δ{m.distance}</p>
                  {similarThumbs[m.submission.id] ? <img src={similarThumbs[m.submission.id]} alt="" className="w-[180px] h-[120px] rounded-lg object-cover" /> : <div className="w-[180px] h-[120px] rounded-lg bg-[#F3F5F8]" />}
                  <p className="text-xs font-bold text-[#0B1B46] mt-1">{m.submission.contributor_first_name}</p>
                </div>
              ))}
            </div>
            <button onClick={onCompareKeep} disabled={busy}
              className="w-full h-11 mt-4 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
              data-testid="host-review-compare-keep-this">Keep this one → archive the rest</button>
          </div>
        </div>
      )}
    </div>
  );
}
